import React, { useContext, useState, useEffect } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isBetween from "dayjs/plugin/isBetween";
import "dayjs/locale/pl";
import axios from "../../axiosConfig";
import OwnerLayout from "./OwnerLayout";
import { OwnerContext } from "../../context/OwnerContext";
import { AiOutlineDelete } from "react-icons/ai";
import { FaChevronDown, FaChevronUp, FaUsers } from "react-icons/fa";
import DeleteConfirmationModal from "../trainer/DeleteConfirmationModal";
import "../../styles/OwnerTrainings.css";

dayjs.locale("pl");
dayjs.extend(isoWeek);
dayjs.extend(isBetween);

const OwnerTrainings = () => {
    const { owner } = useContext(OwnerContext);

    const [dogs, setDogs] = useState([]);
    const [selectedDogId, setSelectedDogId] = useState(null);
    const [trainings, setTrainings] = useState([]);
    const [groupTrainings, setGroupTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [selectedTraining, setSelectedTraining] = useState(null);
    const [schoolSettings, setSchoolSettings] = useState(null);
    const [schoolId, setSchoolId] = useState(null);
    const [openTrainingId, setOpenTrainingId] = useState(null);

    // State for cancel training confirmation modal
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [trainingToCancel, setTrainingToCancel] = useState(null);

    // State for delete additional info confirmation modal
    const [deleteInfoModalOpen, setDeleteInfoModalOpen] = useState(false);
    const [trainingToDeleteInfo, setTrainingToDeleteInfo] = useState(null);

    // Fetch the owner's dogs
    useEffect(() => {
        if (!owner) return;
        const token = localStorage.getItem("jwt");

        axios
            .get(`/api/v1/dog/${owner.id}/list`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const dogList = res.data || [];
                setDogs(dogList);
                // Default: select the first dog if available
                if (dogList.length > 0 && !selectedDogId) {
                    setSelectedDogId(dogList[0].id);
                }

                // Get schoolId from the first dog if available
                if (dogList.length > 0 && dogList[0].schoolId) {
                    setSchoolId(dogList[0].schoolId);
                }
            })
            .catch(() => setError("Nie udaÅ‚o siÄ™ pobraÄ‡ listy pupili."))
            .finally(() => setLoading(false));
    }, [owner, selectedDogId]);

    // Fetch school settings
    useEffect(() => {
        if (!schoolId) return;

        const token = localStorage.getItem("jwt");

        axios
            .get(`/api/v1/school/${schoolId}/settings`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setSchoolSettings(res.data);
            })
            .catch((err) => {
                console.error("Nie udaÅ‚o siÄ™ pobraÄ‡ ustawieÅ„ szkoÅ‚y", err);
            });
    }, [schoolId]);

    // Fetch confirmed trainings for the selected dog
    useEffect(() => {
        if (!selectedDogId) {
            setTrainings([]);
            setGroupTrainings([]);
            return;
        }
        const token = localStorage.getItem("jwt");
        setLoading(true);
        setError("");

        // Define startTime (today) and endTime (end of next month)
        const startTimeParam = dayjs().startOf("day").add(1, "minute").format("YYYY-MM-DDTHH:mm:ss");
        const endTimeParam = dayjs().add(1, "month").endOf("month").format("YYYY-MM-DDTHH:mm:ss");

        // Create promises for both API calls
        const individualTrainingsPromise = axios.get(
            `/api/v1/training/${selectedDogId}/getConfirmedTrainingsForDog`,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: { startTime: startTimeParam, endTime: endTimeParam },
            }
        );

        const groupTrainingsPromise = axios.get(
            `/api/v1/group-training/dog/${selectedDogId}`,
            {
                headers: { Authorization: `Bearer ${token}` },
            }
        );

        // Execute both API calls in parallel
        Promise.all([individualTrainingsPromise, groupTrainingsPromise])
            .then(([individualRes, groupRes]) => {
                setTrainings(individualRes.data || []);
                setGroupTrainings(groupRes.data || []);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching trainings:", error);
                setError("Nie udaÅ‚o siÄ™ pobraÄ‡ treningÃ³w.");
                setLoading(false);
            });
    }, [selectedDogId]);

    const handleDogTabClick = (dogId) => {
        setSelectedDogId(dogId);
        setOpenTrainingId(null);
    };

    // Format date/time display
    const formatDateTime = (startTime) => {
        const d = dayjs(startTime);
        return d.format("DD.MM.YYYY HH:mm");
    };

    const toggleTrainingModal = (trainingId) => {
        setOpenTrainingId(openTrainingId === trainingId ? null : trainingId);
    };

    // Handle additional info modal
    const handleOpenAdditionalInfoModal = (training) => {
        setSelectedTraining(training);
        setInfoModalOpen(true);
    };

    const handleCloseAdditionalInfoModal = () => {
        setSelectedTraining(null);
        setInfoModalOpen(false);
    };

    // Update training's additionalInfo in state
    const handleAdditionalInfoSave = (trainingId, updatedAdditionalInfo) => {
        setTrainings((prevTrainings) =>
            prevTrainings.map((t) =>
                t.id === trainingId ? { ...t, additionalInfo: updatedAdditionalInfo } : t
            )
        );
    };

    // Open confirmation modal for deleting additional info
    const handleOpenDeleteInfoModal = (training) => {
        setTrainingToDeleteInfo(training);
        setDeleteInfoModalOpen(true);
    };

    // Close delete info confirmation modal
    const handleCloseDeleteInfoModal = () => {
        setTrainingToDeleteInfo(null);
        setDeleteInfoModalOpen(false);
    };

    // Confirm delete additional info
    const handleConfirmDeleteAdditionalInfo = async () => {
        if (!trainingToDeleteInfo) return;

        try {
            await axios.delete(`/api/v1/training-additional-info/${trainingToDeleteInfo.additionalInfo.id}`);
            setTrainings((prevTrainings) =>
                prevTrainings.map((t) =>
                    t.id === trainingToDeleteInfo.id ? { ...t, additionalInfo: null } : t
                )
            );
            handleCloseDeleteInfoModal();
        } catch (error) {
            console.error("Error deleting additional info", error);
            handleCloseDeleteInfoModal();
        }
    };

    // Open confirmation modal for canceling training
    const handleOpenCancelModal = (training) => {
        setTrainingToCancel(training);
        setCancelModalOpen(true);
    };

    // Close cancel training confirmation modal
    const handleCloseCancelModal = () => {
        setTrainingToCancel(null);
        setCancelModalOpen(false);
    };

    // Confirm cancel training
    const handleConfirmCancelTraining = async () => {
        if (!trainingToCancel) return;

        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/training/${trainingToCancel.id}/owner-cancel`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Remove the training from the state
            setTrainings((prevTrainings) =>
                prevTrainings.filter((t) => t.id !== trainingToCancel.id)
            );
            handleCloseCancelModal();
        } catch (error) {
            console.error("Error canceling training", error);
            alert("Wystąpił błąd podczas odwoływania treningu");
            handleCloseCancelModal();
        }
    };

    // Check if training can be canceled based on minDaysBeforeCancel setting
    const canCancelTraining = (training) => {
        if (!schoolSettings || !schoolSettings.minDaysBeforeCancel) return true;

        const trainingDate = dayjs(training.startTime);
        const currentDate = dayjs();
        const minCancelDate = currentDate.add(schoolSettings.minDaysBeforeCancel, 'day');

        // Return true if training date is after the minimum cancellation date
        return trainingDate.isAfter(minCancelDate);
    };

    // Check if training is an individual (not group) training
    const isIndividualTraining = (training) => {
        // Individual trainings have trainerDTO object
        // This check distinguishes between individual and group trainings
        return training.trainerDTO !== undefined;
    };

    // Group trainings by time period
    const groupTrainingsByPeriod = (trainings) => {
        const now = dayjs();
        const endOfWeek = now.endOf('isoWeek'); // End of current week (Sunday)

        const thisWeek = [];
        const comingSoon = [];

        trainings.forEach((training) => {
            const trainingDate = dayjs(training.startTime);

            if (trainingDate.isBefore(endOfWeek) || trainingDate.isSame(endOfWeek)) {
                thisWeek.push(training);
            } else {
                comingSoon.push(training);
            }
        });

        return { thisWeek, comingSoon };
    };

    // Combine and sort all trainings
    const allTrainings = [
        ...trainings.map(t => ({ ...t, isGroupTraining: false })),
        ...groupTrainings.map(t => ({ ...t, isGroupTraining: true }))
    ].sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());

    // Group trainings by period
    const { thisWeek, comingSoon } = groupTrainingsByPeriod(allTrainings);

    // Helper to get training name for modal
    const getTrainingName = (training) => {
        if (!training) return "";
        const serviceName = training.serviceName || "Trening";
        const date = dayjs(training.startTime).format("DD.MM.YYYY HH:mm");
        return `${serviceName} - ${date}`;
    };

    return (
        <OwnerLayout>
            <div className="owner-trainings-container">
                <div className="owner-confirmed-trainings-content">
                    {/* Dog selector */}
                    {dogs.length > 1 && (
                        <div className="owner-dog-tabs-row">
                            <div className="owner-dog-tabs-list">
                                {dogs.map((dog) => (
                                    <button
                                        key={dog.id}
                                        onClick={() => handleDogTabClick(dog.id)}
                                        className={dog.id === selectedDogId ? "owner-dog-tab-active" : "owner-dog-tab"}
                                    >
                                        {dog.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {dogs.length === 1 && dogs[0] && (
                        <div className="owner-single-dog-label">
                            <h4>{dogs[0].name}</h4>
                        </div>
                    )}

                    {/* Error and loading states */}
                    {error && <div className="owner-error">{error}</div>}
                    {loading && <div className="owner-loading">Åadowanie...</div>}

                    {/* Training lists */}
                    {!loading && !error && selectedDogId && (
                        <div className="owner-trainings-list">
                            {allTrainings.length === 0 ? (
                                <div className="owner-empty-state">
                                    <h2>Brak treningów</h2>
                                    <img
                                        src="/petboard-graphic.png"
                                        alt="Brak treningów"
                                        className="owner-empty-state-graphic"
                                    />
                                </div>
                            ) : (
                                <>
                                    {/* This Week Section */}
                                    {thisWeek.length > 0 && (
                                        <>
                                            <h3 className="owner-training-section-title">W tym tygodniu</h3>
                                            {thisWeek.map((t) => (
                                                <div key={t.id} className={`owner-training-dropdown-item ${t.isGroupTraining ? 'owner-group-training-item' : ''}`}>
                                                    <div className="owner-training-dropdown-header">
                                                        <div className="owner-training-dropdown-summary" onClick={() => toggleTrainingModal(t.id)}>
                                                            {t.isGroupTraining && <FaUsers className="owner-group-icon" />}
                                                            <span className="owner-training-service">
                                                                {t.isGroupTraining ? 'Trening grupowy' : (t.serviceName || "Trening")}
                                                            </span>
                                                            <span className="owner-training-date">{formatDateTime(t.startTime)}</span>
                                                            <span className="owner-training-trainer">
                                                                {t.isGroupTraining
                                                                    ? t.trainingGroup?.name
                                                                    : `${t.trainerDTO?.firstName || ""} ${t.trainerDTO?.lastName || ""}`
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="owner-training-dropdown-icons">
                                                            {!t.isGroupTraining && canCancelTraining(t) && (
                                                                <AiOutlineDelete
                                                                    className="owner-delete-icon"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenCancelModal(t);
                                                                    }}
                                                                />
                                                            )}
                                                            <div className="owner-chevron-container" onClick={() => toggleTrainingModal(t.id)}>
                                                                {openTrainingId === t.id ? <FaChevronUp className="owner-chevron-icon" /> : <FaChevronDown className="owner-chevron-icon" />}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {openTrainingId === t.id && (
                                                        <div className="owner-training-dropdown-content">
                                                            <div className="owner-training-details">
                                                                {t.isGroupTraining ? (
                                                                    <>
                                                                        <p><strong>Grupa:</strong> {t.trainingGroup?.name}</p>
                                                                        {t.trainingGroup?.description && <p><strong>Opis:</strong> {t.trainingGroup.description}</p>}
                                                                        <p><strong>Liczba psÃ³w:</strong> {t.trainingGroup?.dogsCount}</p>
                                                                        <p><strong>Liczba trenerÃ³w:</strong> {t.trainingGroup?.trainersCount}</p>
                                                                        {t.hallName && <p><strong>Sala:</strong> {t.hallName}</p>}
                                                                        <p><strong>Czas:</strong> {formatDateTime(t.startTime)} - {dayjs(t.endTime).format("HH:mm")}</p>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <p><strong>Trener:</strong> {t.trainerDTO?.firstName} {t.trainerDTO?.lastName}</p>
                                                                        <p><strong>Cena:</strong> {t.price} zł</p>
                                                                        {t.hallName && <p><strong>Sala:</strong> {t.hallName}</p>}
                                                                        <p><strong>Czas:</strong> {formatDateTime(t.startTime)} - {dayjs(t.endTime).format("HH:mm")}</p>
                                                                        {t.additionalInfo && t.additionalInfo.additionalInfo && (
                                                                            <p><strong>Informacje dla trenera:</strong> {t.additionalInfo.additionalInfo}</p>
                                                                        )}

                                                                        {/* Additional info buttons for individual trainings */}
                                                                        <div className="owner-training-actions">
                                                                            {t.additionalInfo ? (
                                                                                <>
                                                                                    <button
                                                                                        className="owner-action-button owner-edit-button"
                                                                                        onClick={() => handleOpenAdditionalInfoModal(t)}
                                                                                    >
                                                                                        Edytuj informacje
                                                                                    </button>
                                                                                    <button
                                                                                        className="owner-action-button owner-delete-info-button"
                                                                                        onClick={() => handleOpenDeleteInfoModal(t)}
                                                                                    >
                                                                                        UsuÅ„ informacje
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <button
                                                                                    className="owner-action-button owner-add-button"
                                                                                    onClick={() => handleOpenAdditionalInfoModal(t)}
                                                                                >
                                                                                    Dodaj informacje dla trenera
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    {/* Coming Soon Section */}
                                    {comingSoon.length > 0 && (
                                        <>
                                            <h3 className="owner-training-section-title">Wkrótce</h3>
                                            {comingSoon.map((t) => (
                                                <div key={t.id} className={`owner-training-dropdown-item ${t.isGroupTraining ? 'owner-group-training-item' : ''}`}>
                                                    <div className="owner-training-dropdown-header">
                                                        <div className="owner-training-dropdown-summary" onClick={() => toggleTrainingModal(t.id)}>
                                                            {t.isGroupTraining && <FaUsers className="owner-group-icon" />}
                                                            <span className="owner-training-service">
                                                                {t.isGroupTraining ? 'Trening grupowy' : (t.serviceName || "Trening")}
                                                            </span>
                                                            <span className="owner-training-date">{formatDateTime(t.startTime)}</span>
                                                            <span className="owner-training-trainer">
                                                                {t.isGroupTraining
                                                                    ? t.trainingGroup?.name
                                                                    : `${t.trainerDTO?.firstName || ""} ${t.trainerDTO?.lastName || ""}`
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="owner-training-dropdown-icons">
                                                            {!t.isGroupTraining && canCancelTraining(t) && (
                                                                <AiOutlineDelete
                                                                    className="owner-delete-icon"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenCancelModal(t);
                                                                    }}
                                                                />
                                                            )}
                                                            <div className="owner-chevron-container" onClick={() => toggleTrainingModal(t.id)}>
                                                                {openTrainingId === t.id ? <FaChevronUp className="owner-chevron-icon" /> : <FaChevronDown className="owner-chevron-icon" />}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {openTrainingId === t.id && (
                                                        <div className="owner-training-dropdown-content">
                                                            <div className="owner-training-details">
                                                                {t.isGroupTraining ? (
                                                                    <>
                                                                        <p><strong>Grupa:</strong> {t.trainingGroup?.name}</p>
                                                                        {t.trainingGroup?.description && <p><strong>Opis:</strong> {t.trainingGroup.description}</p>}
                                                                        <p><strong>Liczba psÃ³w:</strong> {t.trainingGroup?.dogsCount}</p>
                                                                        <p><strong>Liczba trenerÃ³w:</strong> {t.trainingGroup?.trainersCount}</p>
                                                                        {t.hallName && <p><strong>Sala:</strong> {t.hallName}</p>}
                                                                        <p><strong>Czas:</strong> {formatDateTime(t.startTime)} - {dayjs(t.endTime).format("HH:mm")}</p>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <p><strong>Trener:</strong> {t.trainerDTO?.firstName} {t.trainerDTO?.lastName}</p>
                                                                        <p><strong>Cena:</strong> {t.price} zł‚</p>
                                                                        {t.hallName && <p><strong>Sala:</strong> {t.hallName}</p>}
                                                                        <p><strong>Czas:</strong> {formatDateTime(t.startTime)} - {dayjs(t.endTime).format("HH:mm")}</p>
                                                                        {t.additionalInfo && t.additionalInfo.additionalInfo && (
                                                                            <p><strong>Informacje dla trenera:</strong> {t.additionalInfo.additionalInfo}</p>
                                                                        )}

                                                                        {/* Additional info buttons for individual trainings */}
                                                                        <div className="owner-training-actions">
                                                                            {t.additionalInfo ? (
                                                                                <>
                                                                                    <button
                                                                                        className="owner-action-button owner-edit-button"
                                                                                        onClick={() => handleOpenAdditionalInfoModal(t)}
                                                                                    >
                                                                                        Edytuj informacje
                                                                                    </button>
                                                                                    <button
                                                                                        className="owner-action-button owner-delete-info-button"
                                                                                        onClick={() => handleOpenDeleteInfoModal(t)}
                                                                                    >
                                                                                        UsuÅ„ informacje
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <button
                                                                                    className="owner-action-button owner-add-button"
                                                                                    onClick={() => handleOpenAdditionalInfoModal(t)}
                                                                                >
                                                                                    Dodaj informacje dla trenera
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Modal for additional info */}
                {infoModalOpen && selectedTraining && (
                    <TrainingAdditionalInfoModal
                        isOpen={infoModalOpen}
                        onClose={handleCloseAdditionalInfoModal}
                        training={selectedTraining}
                        onSave={handleAdditionalInfoSave}
                    />
                )}

                {/* Confirmation modal for canceling training */}
                <DeleteConfirmationModal
                    isOpen={cancelModalOpen}
                    onClose={handleCloseCancelModal}
                    onConfirm={handleConfirmCancelTraining}
                    itemName={getTrainingName(trainingToCancel)}
                />

                {/* Confirmation modal for deleting additional info */}
                <DeleteConfirmationModal
                    isOpen={deleteInfoModalOpen}
                    onClose={handleCloseDeleteInfoModal}
                    onConfirm={handleConfirmDeleteAdditionalInfo}
                    itemName="informacje dla trenera"
                />
            </div>
        </OwnerLayout>
    );
};

// Modal component for adding/editing additional info
const TrainingAdditionalInfoModal = ({ isOpen, onClose, training, onSave }) => {
    const [content, setContent] = useState(training?.additionalInfo?.additionalInfo || "");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update text area when training changes
    useEffect(() => {
        setContent(training?.additionalInfo?.additionalInfo || "");
    }, [training]);

    const handleSave = async () => {
        if (!content.trim()) {
            alert("ProszÄ™ wprowadziÄ‡ informacje dla trenera");
            return;
        }

        try {
            setIsSubmitting(true);
            let response;

            if (training.additionalInfo) {
                // Update existing additional info
                response = await axios.put("/api/v1/training-additional-info", {
                    id: training.additionalInfo.id,
                    additionalInfo: content,
                });
            } else {
                // Create new additional info
                response = await axios.post("/api/v1/training-additional-info", {
                    trainingId: training.id,
                    additionalInfo: content,
                });
            }

            // Update the training in the list with new additional info
            onSave(training.id, response.data);
            onClose();
        } catch (error) {
            console.error("Error saving additional info", error);
            alert("WystÄ…piÅ‚ bÅ‚Ä…d podczas zapisywania informacji");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="owner-modal-overlay">
            <div className="owner-modal-content">
                <div className="owner-modal-header">
                    <h3>{training.additionalInfo ? "Edytuj informacje dla trenera" : "Dodaj informacje dla trenera"}</h3>
                    <button className="owner-close-button" onClick={onClose}>Ã—</button>
                </div>

                <div className="owner-modal-body">
                    <div className="owner-training-info">
                        <p><strong>Data:</strong> {dayjs(training.startTime).format("DD.MM.YYYY HH:mm")}</p>
                        <p><strong>Trener:</strong> {training.trainerDTO.firstName} {training.trainerDTO.lastName}</p>
                    </div>

                    <div className="owner-form-group">
                        <label htmlFor="additionalInfo">Informacje dla trenera:</label>
                        <textarea
                            id="additionalInfo"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            rows="4"
                            placeholder="WprowadÅº dodatkowe informacje dla trenera..."
                        />
                    </div>
                </div>

                <div className="owner-modal-footer">
                    <button
                        className="owner-cancel-button"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Anuluj
                    </button>
                    <button
                        className="owner-save-button"
                        onClick={handleSave}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Zapisywanie..." : "Zapisz"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OwnerTrainings;